import { useState, useEffect } from 'react';
import { ordersAPI } from '../services/api';
import { notify } from '../utils/notify';
import OrderDetailModal from '../components/OrderDetailModal';
import { HiClipboardList, HiTruck, HiCheck, HiX, HiClock, HiSearch, HiCloudDownload } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import { useCarga } from '../hooks/useCarga';
import ErrorAlCargar from '../components/ErrorAlCargar';
import { mensajeDeError } from '../utils/apiError';
import { dinero } from '../utils/economia';


export default function Orders() {
    const [detailOrderId, setDetailOrderId] = useState(null);
    const [orders, setOrders] = useState([]);
    const { cargando: loading, error, ejecutar, reintentar } = useCarga();
    const [filter, setFilter] = useState('all');
    const [page, setPage] = useState(1);
    // El servidor ya devolvia `pagination: { page, limit, total, pages }` y la
    // pagina se quedaba solo con la lista: `setPage` no se llamaba desde ningun
    // sitio y no habia ningun control. Con mas de veinte pedidos, del 21 en
    // adelante eran inalcanzables y nada en pantalla lo decia.
    const [paginas, setPaginas] = useState(1);
    const [totalPedidos, setTotalPedidos] = useState(0);
    const [sincronizando, setSincronizando] = useState(false);
    const { t } = useTranslation();


    useEffect(() => {
        fetchOrders();
    }, [filter, page]);

    // Al entrar se preguntan las ventas a la tienda. Antes esta pagina solo
    // podia estar vacia: nadie creaba pedidos en toda la aplicacion.
    useEffect(() => {
        let vigente = true;

        ordersAPI
            .sync()
            .then((res) => {
                const r = res.data.data;
                if (vigente && (r.creados > 0 || r.actualizados > 0)) fetchOrders();
            })
            .catch(() => {
                // Que la tienda no conteste no vacia la pagina: se siguen viendo
                // los pedidos que ya se conocian.
            });

        return () => {
            vigente = false;
        };
    }, []);

    const fetchOrders = () =>
        ejecutar(async () => {
            const params = { page, limit: 20 };
            if (filter !== 'all') params.status = filter;
            const response = await ordersAPI.getAll(params);
            setOrders(response.data.data.orders || []);

            const p = response.data.data.pagination || {};
            setPaginas(p.pages || 1);
            setTotalPedidos(p.total || 0);
        });

    const sincronizar = async () => {
        setSincronizando(true);
        try {
            const res = await ordersAPI.sync();
            const r = res.data.data;
            await fetchOrders();
            notify.success(t('orders.sync_done', { creados: r.creados, actualizados: r.actualizados, ajenos: r.ajenos }));
        } catch (error) {
            notify.error(mensajeDeError(error, t('orders.sync_failed')));
        } finally {
            setSincronizando(false);
        }
    };

    const handleFulfill = async (orderId) => {
        try {
            await ordersAPI.fulfill(orderId);
            notify.success(t('orders.fulfill_success'));
            fetchOrders();
        } catch (error) {
            notify.error(mensajeDeError(error, t('orders.fulfill_failed')));
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'delivered': return <HiCheck className="w-4 h-4 text-green-500" />;
            case 'shipped': return <HiTruck className="w-4 h-4 text-blue-500" />;
            case 'pending': return <HiClock className="w-4 h-4 text-yellow-500" />;
            case 'cancelled': return <HiX className="w-4 h-4 text-red-500" />;
            default: return <HiClock className="w-4 h-4 text-slate-500" />;
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'delivered': return 'badge-success';
            case 'shipped': return 'badge-info';
            case 'pending': return 'badge-warning';
            case 'processing': return 'badge-info';
            case 'cancelled':
            case 'refunded': return 'badge-danger';
            default: return 'bg-slate-100 text-slate-600';
        }
    };

    // 'awaiting_shipment' es el estado de toda venta pagada y sin enviar: el
    // mas comun nada mas vender, y faltaba tanto aqui como en las traducciones.
    const statuses = ['all', 'pending', 'awaiting_shipment', 'processing', 'shipped', 'delivered', 'cancelled'];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="page-title flex items-center gap-2">
                        <HiClipboardList className="w-8 h-8 text-blue-500" />
                        {t('orders.title')}
                    </h1>
                    <p className="text-slate-500 dark:text-slate-400 mt-1">
                        {t('orders.subtitle')}
                    </p>
                </div>

                <button
                    type="button"
                    onClick={sincronizar}
                    disabled={sincronizando}
                    className="btn-secondary disabled:opacity-50"
                >
                    <HiCloudDownload className={`w-5 h-5 mr-2 ${sincronizando ? 'animate-pulse' : ''}`} />
                    {t('orders.sync')}
                </button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2">
                {statuses.map((s) => (
                    <button
                        key={s}
                        // Cambiar de filtro vuelve a la primera página: quedarse
                        // en la 3 de un filtro que solo tiene 1 dejaría la lista
                        // vacía sin explicación.
                        onClick={() => { setFilter(s); setPage(1); }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === s
                                ? 'bg-primary-500 text-white'
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                            }`}
                    >
                        {t(`orders.status_${s}`)}
                    </button>
                ))}
            </div>

            {/* Orders Table */}
            {loading ? (
                <div className="card">
                    <div className="space-y-4">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="h-16 bg-slate-200 dark:bg-slate-700 rounded animate-pulse"></div>
                        ))}
                    </div>
                </div>
            ) : error ? (
                <ErrorAlCargar error={error} onReintentar={reintentar} />
            ) : orders.length > 0 ? (
                <div className="card overflow-hidden p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-slate-50 dark:bg-slate-700/50">
                                <tr className="text-left text-sm text-slate-500 dark:text-slate-400">
                                    <th className="px-6 py-4 font-medium">{t('orders.column_order')}</th>
                                    <th className="px-6 py-4 font-medium">{t('orders.column_customer')}</th>
                                    <th className="px-6 py-4 font-medium">{t('orders.column_store')}</th>
                                    <th className="px-6 py-4 font-medium">{t('orders.column_amount')}</th>
                                    <th className="px-6 py-4 font-medium">{t('orders.column_profit')}</th>
                                    <th className="px-6 py-4 font-medium">{t('orders.column_status')}</th>
                                    <th className="px-6 py-4 font-medium">{t('orders.column_date')}</th>
                                    <th className="px-6 py-4 font-medium">{t('orders.column_actions')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                {orders.map((order) => (
                                    <tr key={order.id} className="text-sm hover:bg-slate-50 dark:hover:bg-slate-700/30">
                                        <td className="px-6 py-4">
                                            <span className="font-medium text-slate-900 dark:text-white">
                                                #{order.order_number || order.id.slice(0, 8)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div>
                                                <p className="text-slate-900 dark:text-white">{order.customer_name || 'N/A'}</p>
                                                <p className="text-xs text-slate-500">{order.customer_email}</p>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-slate-600 dark:text-slate-400">
                                            {order.store?.store_name || 'N/A'}
                                        </td>
                                        {/* Un pedido sin total no es una venta de
                                            cero: es una venta cuyo importe no
                                            mando eBay. El `|| 0` que habia aqui
                                            las hacia indistinguibles. */}
                                        <td className="px-6 py-4 font-medium text-slate-900 dark:text-white">
                                            {order.total_amount === null || order.total_amount === undefined ? (
                                                <span className="text-slate-400 text-sm font-normal">{t('orders.total_unknown')}</span>
                                            ) : (
                                                dinero(Number(order.total_amount))
                                            )}
                                        </td>
                                        {/* Una ganancia sin las comisiones de eBay
                                            restadas es optimista, y sin el coste
                                            del envio no existe. Se dice cual es
                                            cual en lugar de ensenar un numero
                                            redondo que no lo es. */}
                                        <td className="px-6 py-4 font-medium">
                                            {order.profit === null || order.profit === undefined ? (
                                                <span className="text-slate-400 text-sm">{t('orders.profit_unknown')}</span>
                                            ) : (
                                                <>
                                                    <span className={Number(order.profit) >= 0 ? 'text-green-500' : 'text-red-500'}>
                                                        {Number(order.profit) < 0 ? '-' : '+'}${Math.abs(Number(order.profit)).toFixed(2)}
                                                    </span>
                                                    {(order.marketplace_fee === null || order.marketplace_fee === undefined) && (
                                                        <span className="block text-[11px] font-normal text-slate-400">
                                                            {t('orders.before_fees')}
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`badge ${getStatusBadge(order.status)} inline-flex items-center gap-1`}>
                                                {getStatusIcon(order.status)}
                                                {t(`orders.status_${order.status}`)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-slate-500">
                                            {new Date(order.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex gap-1">
                                                <button
                                                    onClick={() => setDetailOrderId(order.id)}
                                                    title={t('orders.view_detail')}
                                                    className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                                                >
                                                    <HiSearch className="w-4 h-4 text-slate-500" />
                                                </button>
                                                {/* También en `awaiting_shipment`,
                                                    que es el estado de toda venta
                                                    pagada y sin enviar — el más
                                                    común nada más vender. El
                                                    filtro de arriba ya lo conocía
                                                    y este botón no: una venta
                                                    pagada se listaba sin ninguna
                                                    acción posible.

                                                    Y con su nombre: era el único
                                                    icono de la tabla sin `title`
                                                    ni `aria-label`, o sea un
                                                    camión mudo. */}
                                                {['pending', 'awaiting_shipment'].includes(order.status) && (
                                                    <button
                                                        onClick={() => handleFulfill(order.id)}
                                                        title={t('orders.fulfill')}
                                                        aria-label={t('orders.fulfill')}
                                                        className="p-2 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/20 text-green-500"
                                                    >
                                                        <HiTruck className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* El paso de páginas, que no existía.
                        Va debajo de la tabla porque es donde termina de leerse
                        una lista, que es cuando se busca «lo siguiente». */}
                    {paginas > 1 && (
                        <div className="flex items-center justify-between gap-4 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
                            <span className="text-sm text-slate-500">
                                {t('orders.page_of', { page, pages: paginas, total: totalPedidos })}
                            </span>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page <= 1}
                                    className="btn-secondary btn-sm disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    {t('orders.previous_page')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setPage((p) => Math.min(paginas, p + 1))}
                                    disabled={page >= paginas}
                                    className="btn-secondary btn-sm disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    {t('orders.next_page')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="card text-center py-16">
                    <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                        <HiClipboardList className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">{t('orders.no_orders_title')}</h3>
                    <p className="text-slate-500 max-w-md mx-auto">
                        {t('orders.no_orders_desc')}
                    </p>
                </div>
            )}

            <OrderDetailModal
                isOpen={Boolean(detailOrderId)}
                orderId={detailOrderId}
                onClose={() => setDetailOrderId(null)}
            />
        </div>
    );
}
